import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Car, MapPin, Calendar, Clock, DollarSign, Users, 
  ChevronRight, Navigation, AlertCircle, Check, Phone, User, Plus, History, Star, Eye,
  Download, FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';
import RatingModal from '../common/RatingModal';
import LiveTrackingMap from '../common/LiveTrackingMap';

const RideBookingTab = () => {
  const [step, setStep] = useState('select'); // select, booking, confirmation
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rideToRate, setRideToRate] = useState(null);
  const [trackingRide, setTrackingRide] = useState(null);
  
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    ride_type: 'one_way',
    pickup_location: {
      address: '',
      city: '',
      state: 'NC',
      zip_code: '',
      notes: ''
    },
    dropoff_location: {
      address: '',
      city: '',
      state: 'NC',
      zip_code: '',
      notes: ''
    },
    pickup_datetime: '',
    is_asap: false,
    passenger_count: 1,
    has_luggage: false,
    assistance_needed: [],
    preferred_vehicle_type: 'sedan',
    special_instructions: '',
    payment_method: 'card'
  });

  useEffect(() => {
    fetchMyRides();
  }, []);

  const fetchMyRides = async () => {
    try {
      const response = await api.get('/rides/my-rides');
      setRides(response.data || []);
    } catch (error) {
      console.error('Error fetching rides:', error);
    }
    setLoading(false);
  };

  // Export ride history as CSV
  const exportRideHistory = async () => {
    try {
      const response = await api.get('/rides/export/csv', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ride_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Ride history exported successfully');
    } catch (error) {
      toast.error('Failed to export ride history');
    }
  };

  // Download individual ride receipt
  const downloadReceipt = async (ride) => {
    try {
      const response = await api.get(`/rides/export/receipt/${ride.id}`);
      const receipt = response.data;
      
      // Generate receipt HTML
      const receiptHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ride Receipt - ${receipt.receipt_number}</title>
          <style>
            body { font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #D4AF37; padding-bottom: 20px; margin-bottom: 20px; }
            .header h1 { color: #D4AF37; margin: 0; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; color: #333; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .label { color: #666; }
            .value { font-weight: 500; }
            .total { font-size: 1.2em; color: #D4AF37; border-top: 2px solid #D4AF37; padding-top: 10px; margin-top: 10px; }
            .footer { text-align: center; color: #999; font-size: 12px; margin-top: 30px; border-top: 1px solid #eee; padding-top: 20px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${receipt.company}</h1>
            <p>Ride Receipt</p>
          </div>
          
          <div class="section">
            <div class="row"><span class="label">Receipt #:</span><span class="value">${receipt.receipt_number}</span></div>
            <div class="row"><span class="label">Date:</span><span class="value">${receipt.date} ${receipt.time}</span></div>
            <div class="row"><span class="label">Status:</span><span class="value">${receipt.status}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Trip Details</div>
            <div class="row"><span class="label">From:</span><span class="value">${receipt.trip.pickup.address}, ${receipt.trip.pickup.city}</span></div>
            <div class="row"><span class="label">To:</span><span class="value">${receipt.trip.dropoff.address}, ${receipt.trip.dropoff.city}</span></div>
            <div class="row"><span class="label">Distance:</span><span class="value">${receipt.trip.distance_miles} miles</span></div>
            <div class="row"><span class="label">Duration:</span><span class="value">${receipt.trip.duration_minutes || '—'} min</span></div>
            <div class="row"><span class="label">Ride Type:</span><span class="value">${receipt.trip.ride_type}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Driver</div>
            <div class="row"><span class="label">Name:</span><span class="value">${receipt.driver.name}</span></div>
            ${receipt.driver.vehicle ? `<div class="row"><span class="label">Vehicle:</span><span class="value">${receipt.driver.vehicle}</span></div>` : ''}
          </div>
          
          <div class="section">
            <div class="section-title">Fare Breakdown</div>
            <div class="row"><span class="label">Base Fare:</span><span class="value">$${receipt.fare_breakdown.base_fare.toFixed(2)}</span></div>
            ${receipt.fare_breakdown.wait_time_fee > 0 ? `<div class="row"><span class="label">Wait Time (${receipt.fare_breakdown.wait_time_minutes} min):</span><span class="value">$${receipt.fare_breakdown.wait_time_fee.toFixed(2)}</span></div>` : ''}
            ${receipt.fare_breakdown.addons_total > 0 ? `<div class="row"><span class="label">Add-ons:</span><span class="value">$${receipt.fare_breakdown.addons_total.toFixed(2)}</span></div>` : ''}
            ${receipt.fare_breakdown.discount > 0 ? `<div class="row"><span class="label">Discount:</span><span class="value">-$${receipt.fare_breakdown.discount.toFixed(2)}</span></div>` : ''}
            <div class="row total"><span class="label">Total:</span><span class="value">$${receipt.fare_breakdown.total_fare.toFixed(2)}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Payment</div>
            <div class="row"><span class="label">Method:</span><span class="value">${receipt.payment.method}</span></div>
            <div class="row"><span class="label">Status:</span><span class="value">${receipt.payment.status}</span></div>
          </div>
          
          <div class="footer">
            <p>Thank you for riding with NC Help Hub!</p>
            <p>Questions? Contact us at support@nchelphub.com</p>
          </div>
        </body>
        </html>
      `;
      
      // Open in new window for printing/saving
      const printWindow = window.open('', '_blank');
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
      
      toast.success('Receipt opened - use Ctrl+P to print or save as PDF');
    } catch (error) {
      toast.error('Failed to generate receipt');
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setBookingData(prev => ({ ...prev, [field]: value }));
    }
  };

  const estimateFare = async () => {
    // Extract zip codes safely, handling both nested and flat structures
    const pickupZip = bookingData.pickup_location?.zip_code || bookingData.pickup_zip || '';
    const dropoffZip = bookingData.dropoff_location?.zip_code || bookingData.dropoff_zip || '';
    
    if (!pickupZip || !dropoffZip) {
      toast.error('Please enter pickup and dropoff ZIP codes');
      return;
    }
    
    setEstimating(true);
    try {
      // Use POST body for better reliability
      const response = await api.post('/rides/estimate-fare', {
        pickup_zip: pickupZip,
        dropoff_zip: dropoffZip,
        vehicle_type: bookingData.preferred_vehicle_type || 'sedan',
        passenger_count: bookingData.passenger_count || 1,
        extra_stops: 0,
        needs_assistance: (bookingData.assistance_needed || []).length > 0,
        is_round_trip: bookingData.ride_type === 'round_trip'
      });
      setEstimate(response.data);
    } catch (error) {
      console.error('Fare estimate error:', error);
      toast.error('Could not estimate fare');
    }
    setEstimating(false);
  };

  const handleSubmitBooking = async () => {
    // Validate required fields
    if (!bookingData.customer_name || !bookingData.customer_phone) {
      toast.error('Please provide your name and phone number');
      return;
    }
    if (!bookingData.pickup_location.address || !bookingData.dropoff_location.address) {
      toast.error('Please provide pickup and dropoff addresses');
      return;
    }
    if (!bookingData.pickup_datetime && !bookingData.is_asap) {
      toast.error('Please select a pickup time or choose ASAP');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...bookingData,
        estimated_fare: estimate?.estimated_fare || null,
        pickup_datetime: bookingData.is_asap 
          ? new Date().toISOString() 
          : new Date(bookingData.pickup_datetime).toISOString()
      };
      
      const response = await api.post('/rides/request', payload);
      toast.success('Ride request submitted!');
      setStep('confirmation');
      fetchMyRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ride request');
    }
    setSubmitting(false);
  };

  const getStatusBadge = (status) => {
    const configs = {
      requested: { color: 'bg-blue-100 text-blue-700', label: 'Requested' },
      pending_driver: { color: 'bg-yellow-100 text-yellow-700', label: 'Finding Driver' },
      driver_assigned: { color: 'bg-purple-100 text-purple-700', label: 'Driver Assigned' },
      en_route_pickup: { color: 'bg-indigo-100 text-indigo-700', label: 'Driver En Route' },
      arrived_pickup: { color: 'bg-cyan-100 text-cyan-700', label: 'Driver Arrived' },
      in_progress: { color: 'bg-orange-100 text-orange-700', label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled' }
    };
    const config = configs[status] || configs.requested;
    return <span className={`px-2 py-1 rounded-full text-xs ${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ride-booking-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Car className="w-6 h-6 text-[var(--primary)]" />
            Book a Ride
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Safe, reliable transportation services
          </p>
        </div>
      </div>

      {/* Action Tabs */}
      <div className="flex gap-2">
        <Button
          variant={step === 'select' || step === 'booking' ? 'default' : 'outline'}
          onClick={() => setStep('select')}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Ride
        </Button>
        <Button
          variant={step === 'history' ? 'default' : 'outline'}
          onClick={() => setStep('history')}
        >
          <History className="w-4 h-4 mr-2" />
          My Rides ({rides.length})
        </Button>
      </div>

      {/* New Ride Booking Form */}
      {(step === 'select' || step === 'booking') && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6 space-y-6"
        >
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-[var(--primary)]" />
              Your Information
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  value={bookingData.customer_name}
                  onChange={(e) => handleChange('customer_name', e.target.value)}
                  placeholder="Your name"
                  data-testid="ride-customer-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <Input
                  value={bookingData.customer_phone}
                  onChange={(e) => handleChange('customer_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="ride-customer-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={bookingData.customer_email}
                  onChange={(e) => handleChange('customer_email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
            </div>
          </div>

          {/* Pickup Location */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              Pickup Location
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Address *</label>
                <Input
                  value={bookingData.pickup_location.address}
                  onChange={(e) => handleChange('pickup_location.address', e.target.value)}
                  placeholder="123 Main Street"
                  data-testid="ride-pickup-address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <Input
                  value={bookingData.pickup_location.city}
                  onChange={(e) => handleChange('pickup_location.city', e.target.value)}
                  placeholder="Raleigh"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                <Input
                  value={bookingData.pickup_location.zip_code}
                  onChange={(e) => handleChange('pickup_location.zip_code', e.target.value)}
                  placeholder="27601"
                  data-testid="ride-pickup-zip"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Notes for Driver</label>
                <Input
                  value={bookingData.pickup_location.notes}
                  onChange={(e) => handleChange('pickup_location.notes', e.target.value)}
                  placeholder="e.g., Ring doorbell, meet at lobby..."
                />
              </div>
            </div>
          </div>

          {/* Dropoff Location */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500"></div>
              Dropoff Location
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Address *</label>
                <Input
                  value={bookingData.dropoff_location.address}
                  onChange={(e) => handleChange('dropoff_location.address', e.target.value)}
                  placeholder="456 Oak Avenue"
                  data-testid="ride-dropoff-address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <Input
                  value={bookingData.dropoff_location.city}
                  onChange={(e) => handleChange('dropoff_location.city', e.target.value)}
                  placeholder="Durham"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                <Input
                  value={bookingData.dropoff_location.zip_code}
                  onChange={(e) => handleChange('dropoff_location.zip_code', e.target.value)}
                  placeholder="27701"
                  data-testid="ride-dropoff-zip"
                />
              </div>
            </div>
          </div>

          {/* Ride Details */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[var(--primary)]" />
              Ride Details
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">When</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={bookingData.is_asap}
                      onChange={(e) => handleChange('is_asap', e.target.checked)}
                      className="accent-[#D4AF37]"
                    />
                    <span className="text-sm">As soon as possible</span>
                  </label>
                  {!bookingData.is_asap && (
                    <Input
                      type="datetime-local"
                      value={bookingData.pickup_datetime}
                      onChange={(e) => handleChange('pickup_datetime', e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vehicle Type</label>
                <select
                  className="w-full p-2 border rounded-lg bg-white"
                  value={bookingData.preferred_vehicle_type}
                  onChange={(e) => handleChange('preferred_vehicle_type', e.target.value)}
                  data-testid="ride-vehicle-type"
                >
                  <option value="sedan">Car (1-4 passengers)</option>
                  <option value="suv">SUV (1-6 passengers)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Passengers</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={bookingData.passenger_count}
                  onChange={(e) => handleChange('passenger_count', parseInt(e.target.value))}
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={bookingData.has_luggage}
                  onChange={(e) => handleChange('has_luggage', e.target.checked)}
                  className="accent-[#D4AF37]"
                />
                <span className="text-sm">Luggage/Extra Items</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={bookingData.assistance_needed.includes('door_to_door')}
                  onChange={(e) => {
                    const newAssistance = e.target.checked 
                      ? [...bookingData.assistance_needed, 'door_to_door']
                      : bookingData.assistance_needed.filter(a => a !== 'door_to_door');
                    handleChange('assistance_needed', newAssistance);
                  }}
                  className="accent-[#D4AF37]"
                />
                <span className="text-sm">Door-to-Door Assistance</span>
              </label>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Special Instructions</label>
              <textarea
                className="w-full p-3 border rounded-lg"
                rows="2"
                value={bookingData.special_instructions}
                onChange={(e) => handleChange('special_instructions', e.target.value)}
                placeholder="Any special needs or instructions for the driver..."
              />
            </div>
          </div>

          {/* Fare Estimate */}
          <div className="bg-[var(--background-subtle)] p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[var(--primary)]" />
                Fare Estimate
              </h3>
              <Button variant="outline" size="sm" onClick={estimateFare} disabled={estimating}>
                {estimating ? 'Calculating...' : 'Get Estimate'}
              </Button>
            </div>
            
            {estimate && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-[var(--primary)]">${estimate.estimated_fare?.toFixed(2)}</p>
                  <p className="text-xs text-[var(--text-muted)]">Estimated Fare</p>
                </div>
                <div>
                  <p className="text-lg font-semibold">{estimate.estimated_miles?.toFixed(1)} mi</p>
                  <p className="text-xs text-[var(--text-muted)]">Distance</p>
                </div>
                <div>
                  <p className="text-lg font-semibold">{estimate.estimated_minutes} min</p>
                  <p className="text-xs text-[var(--text-muted)]">Est. Time</p>
                </div>
                <div>
                  <p className="text-lg font-semibold capitalize">{bookingData.preferred_vehicle_type}</p>
                  <p className="text-xs text-[var(--text-muted)]">Vehicle</p>
                </div>
              </div>
            )}
            
            {!estimate && (
              <p className="text-sm text-[var(--text-muted)] text-center">
                Enter ZIP codes and click "Get Estimate" to see fare
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              className="flex-1"
              onClick={handleSubmitBooking}
              disabled={submitting}
              data-testid="submit-ride-request"
            >
              {submitting ? 'Submitting...' : 'Request Ride'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Confirmation */}
      {step === 'confirmation' && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-8 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Ride Request Submitted!</h3>
          <p className="text-[var(--text-muted)] mb-6">
            We'll find you a driver and notify you when they're on their way.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => setStep('history')}>
              View My Rides
            </Button>
            <Button onClick={() => { setStep('select'); setEstimate(null); }}>
              Book Another Ride
            </Button>
          </div>
        </motion.div>
      )}

      {/* Ride History */}
      {step === 'history' && (
        <div className="space-y-4">
          {/* Export Controls */}
          {rides.length > 0 && (
            <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold">Ride History</h3>
                <p className="text-sm text-[var(--text-muted)]">{rides.length} ride{rides.length !== 1 ? 's' : ''}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportRideHistory}
                className="border-[var(--primary)] text-[var(--primary)]"
                data-testid="export-rides-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          )}
          
          {rides.length === 0 ? (
            <div className="card p-12 text-center">
              <Car className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
              <h3 className="text-lg font-semibold mb-2">No Rides Yet</h3>
              <p className="text-[var(--text-muted)] mb-4">Book your first ride to get started!</p>
              <Button onClick={() => setStep('select')}>
                <Plus className="w-4 h-4 mr-2" />
                Book a Ride
              </Button>
            </div>
          ) : (
            rides.map(ride => (
              <div key={ride.id} className="card p-4" data-testid={`my-ride-${ride.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="font-mono text-sm text-[var(--primary)]">{ride.reference}</span>
                    <p className="text-xs text-[var(--text-muted)]">
                      {new Date(ride.pickup_datetime).toLocaleString()}
                    </p>
                  </div>
                  {getStatusBadge(ride.status)}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                    <span>{ride.pickup_location?.address}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500 mt-1"></div>
                    <span>{ride.dropoff_location?.address}</span>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                  <span className="text-[var(--text-muted)]">
                    {ride.passenger_count} passenger{ride.passenger_count > 1 ? 's' : ''}
                  </span>
                  <span className="font-semibold text-[var(--primary)]">
                    ${ride.estimated_fare?.toFixed(2) || '—'}
                  </span>
                </div>
                
                {ride.driver_name && (
                  <div className="mt-2 p-2 bg-[var(--background-subtle)] rounded flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--primary)]" />
                    <span className="text-sm">Driver: {ride.driver_name}</span>
                  </div>
                )}
                
                {/* Track button for active rides */}
                {['en_route_pickup', 'arrived_pickup', 'in_progress', 'driver_assigned'].includes(ride.status) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                    onClick={() => setTrackingRide(ride)}
                    data-testid={`track-ride-${ride.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Track Ride
                  </Button>
                )}
                
                {/* Rate button for completed rides */}
                {ride.status === 'completed' && !ride.rating && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    onClick={() => {
                      setRideToRate(ride);
                      setShowRatingModal(true);
                    }}
                    data-testid={`rate-ride-${ride.id}`}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Rate Your Ride
                  </Button>
                )}
                
                {/* Show existing rating */}
                {ride.rating && (
                  <div className="mt-3 flex items-center gap-1 text-sm text-[#D4AF37]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < ride.rating ? 'fill-[#D4AF37]' : ''}`} />
                    ))}
                    <span className="ml-2 text-[var(--text-muted)]">Your rating</span>
                  </div>
                )}
                
                {/* Download Receipt button for completed rides */}
                {ride.status === 'completed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full text-[var(--text-muted)] hover:text-[var(--primary)]"
                    onClick={() => downloadReceipt(ride)}
                    data-testid={`download-receipt-${ride.id}`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download Receipt
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Rating Modal */}
      {showRatingModal && rideToRate && (
        <RatingModal
          ride={rideToRate}
          type="ride"
          onClose={() => {
            setShowRatingModal(false);
            setRideToRate(null);
          }}
          onSubmitted={() => {
            fetchMyRides();
          }}
        />
      )}
      
      {/* Live Tracking Modal */}
      {trackingRide && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold">Track Your Ride</h3>
              <Button variant="outline" size="sm" onClick={() => setTrackingRide(null)}>Close</Button>
            </div>
            <div className="p-4">
              <LiveTrackingMap 
                ride={trackingRide}
                driverLocation={trackingRide.driver_current_location}
                pickupCoords={trackingRide.pickup_coords}
                dropoffCoords={trackingRide.dropoff_coords}
                onRefresh={fetchMyRides}
                showFullMap={true}
              />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RideBookingTab;
